import React, { useState, useEffect } from "react";
import {
  CssBaseline,
  ThemeProvider,
  useMediaQuery,
} from "@material-ui/core";
import { createTheme } from '@material-ui/core/styles'
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import Login from "./App/Auth/Login";
import { Toaster } from 'react-hot-toast';
import Main from "./App/Main/Main";
import ForgotPassword from "./App/Auth/ForgotPassword";
import NewPassword from "./App/Auth/NewPassword";
import { isAuth } from "./helpers/AuthHelpers"
import ProductPage from "../src/Customization/ProductPage";
import "./App.css";

// Theme configuration function
const getThemeConfig = (mode) => {
  return createTheme({
    palette: {
      mode: mode,
      primary: {
        main: mode === 'dark' ? '#90caf9' : '#1976d2',
      },
      secondary: {
        main: mode === 'dark' ? '#f48fb1' : '#dc004e',
      },
      background: {
        default: mode === 'dark' ? '#121212' : '#f5f5f5',
        paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#ffffff' : '#000000',
        secondary: mode === 'dark' ? '#b0b0b0' : '#666666',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none', // Remove gradient for dark mode compatibility
          },
        },
      },
    },
  });
};

function App() {
  // Get initial theme from localStorage or time-based

  const getInitialTheme = () => {
    const storedTheme = localStorage.getItem("theme");
    if (storedTheme) return storedTheme;
    
    const hour = new Date().getHours();
    return hour > 18 || hour < 6 ? "dark" : "light";
  };

  const [themeMode, setThemeMode] = useState(getInitialTheme);
  const prefersDarkMode = useMediaQuery(`(prefers-color-scheme: ${themeMode})`);

  // Create theme based on mode
  const theme = React.useMemo(
    () => getThemeConfig(themeMode),
    [themeMode]
  );

  // Sync theme with localStorage
  useEffect(() => {
    localStorage.setItem("theme", themeMode);
  }, [themeMode]);

  // Toggle theme function
  const toggleTheme = () => {
    setThemeMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };



  // Protected Route component
  const ProtectedRoute = ({ component: Component, ...rest }) => {
    return (
      <Route
        {...rest}
        render={(props) =>
          isAuth ? (
            <Component {...props} toggleTheme={toggleTheme} themeMode={themeMode} />
          ) : (
            <Redirect to="/" />
          )
        }
      />
    );
  };

  // Public Route component (redirects if already authenticated)
  const PublicRoute = ({ component: Component, ...rest }) => {
    return (
      <Route
        {...rest}
        render={(props) =>
          !isAuth ? (
            <Component {...props} toggleTheme={toggleTheme} themeMode={themeMode} />
          ) : (
            <Redirect to="/dashboard" />
          )
        }
      />
    );
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <CssBaseline />
    
          <BrowserRouter>
            <Switch>
              {/* Public Routes */}
              <PublicRoute exact path="/" component={Login} />
              <PublicRoute path="/forgot-password" component={ForgotPassword} />
              <PublicRoute path="/reset-password/:token" component={NewPassword} />

              {/* Protected Routes */}
              <ProtectedRoute path="/customize/:id/product" component={ProductPage} />
              <ProtectedRoute path="/dashboard" component={Main} />
              <ProtectedRoute path="/" component={Main} />

              {/* Catch-all redirect */}
              <Route path="*">
                <Redirect to={isAuth ? "/dashboard" : "/"} />
              </Route>
            </Switch>
          </BrowserRouter>
      
      </ThemeProvider>

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          },
          success: {
            iconTheme: {
              primary: theme.palette.success.main,
              secondary: theme.palette.getContrastText(theme.palette.success.main),
            },
          },
          error: {
            iconTheme: {
              primary: theme.palette.error.main,
              secondary: theme.palette.getContrastText(theme.palette.error.main),
            },
          },
        }}
      />
    </>
  );
}

export default App;